/**
 * interior_life — RESIDENTS WHO USE THE WHOLE BUILDING, pure (the interiors programme, slice L, 2026-10-01).
 *
 * A resident at home used to be PLACED: one spot per (resident, state), popped between floors, two residents on one
 * socket, a standing clip on a sofa. This is the ITINERARY that replaces the placement while a player is inside: which
 * anchor each resident is at or walking between, the walk along the planner's own routes, the stair runs and the lift,
 * and where on that walk they are at time t — a pure function of things every client holds:
 *
 *   the ROOM        the descriptor's routed anchors at the canonical detail (beds, seats, work points, public standing
 *                   points — each with the polyline the planner proved from its storey's way in), the stair runs, the
 *                   storeys the lift serves;
 *   the RESIDENTS   id and the CLASS of `versona_state.state` (asleep · awake · at play). NEVER the row's stamp:
 *                   THE STAMP IS NOT A CLOCK — the server rewrites a resident's row every few seconds while they are
 *                   at home (`resting` ↔ `chatting` flips with company: over 2,334 live rows the median age was 6–13 s,
 *                   measured 2026-10-02), so nothing here may hang on "since when" or on which of the two it is;
 *   the CLOCK       wall seconds, and the sky's hour at any instant (`timeOfDay` + `cycleSeconds` + the sky row's stamp).
 *
 * THE RULES
 *   · SLOTS         time is cut into `SLOT_S` (5 min) slots. Residents are dealt in id order, each taking the FREE
 *                   anchor it prefers (a hash) among the kinds its class and the hour name. A sleeper keeps its bed;
 *                   everyone else moves every `MOVE_EVERY` slots, half of them at each boundary (a phase per id).
 *                   NO TWO RESIDENTS SHARE AN ANCHOR.
 *   · LEAVING FIRST nobody ARRIVES at an anchor before its last user has set off from it: a mover's departure is
 *                   pushed back until its walk ends after that user's begins (a chain waits in order; two who swap
 *                   seats leave together and pass each other).
 *   · THE WALK      the two entry-rooted polylines joined at their last common point (every segment is one the planner
 *                   proved), a stair run between storeys, and the lift for two storeys or more where it serves both —
 *                   ONE car: rides are reserved in (ready, id) order and a later rider waits at the call post.
 *   · A CHANGE      a resident who comes home, leaves, wakes or turns in is simply at their anchor from that moment
 *                   (no walk can be timed without a clock every client shares — see THE STAMP IS NOT A CLOCK); the
 *                   walks are the slot boundaries'.
 *   · NEVER MIME    `rig` says what is under the body: a seated clip only on a drawn seat, a lying clip only on a drawn
 *                   bed; a piece this client does not draw (a lower detail level) is not sat on — the body stands
 *                   where it would be; a walker walks, a rider stands.
 *
 * The lever (`localStorage atlas:interior-life = 'off'`), the planner's lever, a legacy room, or a room with no routed
 * anchor → `lifeRoomOf` answers null and the caller keeps today's placed residents.
 */
import type { InteriorSpec } from './building_interiors.js';
import type { InteriorDescriptor } from './interior_descriptor.js';
import { floorY, rideProfile, cabRideRect, LIFT, LIFT_HZ, type LiftService } from './interior_lift.js';

/** The states a versona is AT HOME in (`home_layout.HOME_STATES`, mirrored here so this file stays free of the layout
 *  code; the gate compares the two). Anyone else is OUT: never dealt an anchor, never drawn inside. */
export const LIFE_HOME_STATES: ReadonlySet<string> = new Set(['resting', 'sleeping', 'playing', 'chatting']);

export const LIFE = {
  SLOT_S: 300,
  /** A mover reaches an anchor at least this long after its last user set off from it. */
  LEAVE_GAP_S: 0.1,
  /** An awake resident keeps an anchor this many slots (half of them move at each boundary). */
  MOVE_EVERY: 2,
  /** By day, how much a bed weighs against a seat (in tenths) when an awake resident picks what to rest on. */
  NAP_TENTHS: 1,
  WALK_MPS: 1.25, ELDER_MPS: 0.8, CHILD_MPS: 1.6, RAMP_MPS: 0.9,
  /** Sitting down / getting up: the step between a seat and the standing point beside it. */
  SEAT_S: 0.9,
  /** Walking between the call post and the car. */
  BOARD_S: 1.6,
  /** A spread of departures inside the leaving window. */
  STAGGER_S: 40,
} as const;
export const LIFE_LEVER_KEY = 'atlas:interior-life';

let lifeAt = -1e9, lifeOn = true;
/** Is the itinerary on? Default ON; `localStorage atlas:interior-life = 'off'` = today's placed residents. */
export function interiorLifeEnabled(read?: (k: string) => string | null): boolean {
  if (read) return read(LIFE_LEVER_KEY) !== 'off';
  const now = typeof performance !== 'undefined' ? performance.now() : 0;
  if (now - lifeAt < 1000) return lifeOn;
  lifeAt = now;
  try {
    const ls = (globalThis as unknown as { localStorage?: { getItem(k: string): string | null } }).localStorage;
    lifeOn = !ls || ls.getItem(LIFE_LEVER_KEY) !== 'off';
  } catch { lifeOn = true; }
  return lifeOn;
}

// ─── Hashing (THE HASH LAW: bare FNV is arithmetic in its last character — finish it) ────────────────────────────────

export function lifeHash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return h >>> 0;
}

// ─── The room ────────────────────────────────────────────────────────────────────────────────────────────────────────

export type AnchorUse = 'lie' | 'sit' | 'work' | 'stand';
type Pt = [number, number];
export interface LifeAnchor {
  /** `<floor>:<anchor id>` — unique in the building. */
  key: string;
  floor: number; use: AnchorUse;
  /** Where the body is when settled (building-local), the yaw it faces, its height above the storey's floor. */
  x: number; z: number; yaw: number; lift: number;
  /** The standing point the walk ends at, and the polyline from the storey's way in to it. */
  ax: number; az: number; route: Pt[];
  /** False = this client does not draw the piece under it (a lower detail level): the body stands at (x, z), on the floor. */
  drawn: boolean;
  /** A WORK point with a seat of its own (a desk's chair): the body sits there to work. */
  seated?: boolean;
}
export interface LifeRoom {
  floors: number;
  anchors: LifeAnchor[];
  /** Per storey: the planner's polyline from the way in to the stair foot / to the lift's call post (null = none). */
  toFoot: Array<Pt[] | null>; toCall: Array<Pt[] | null>;
  /** Per storey: the way in (the door; the head of the stair run). */
  root: Array<{ x: number; z: number } | null>;
  /** The stair run: its x, where its ramp starts and ends in z. */
  stair: { x: number; z0: number; z1: number };
  /** The lift, when it runs: the storeys it serves, the call post, where a rider stands in the car, the yaw that faces the door. */
  lift: { served: number[]; call: { x: number; z: number }; cab: { x: number; z: number }; faceDoor: number } | null;
}

export interface LifeSocket { pose: 'sit' | 'lie'; x: number; z: number; floor: number; lift: number; slot: string }
/**
 * The room the itinerary walks: the descriptor's ROUTED anchors on its PLANNED storeys.
 * `sockets` = the resident sockets of the CANONICAL view (full detail, the bought pack) — the same on every client;
 * `shown(floor, slot)` = is that piece in the canonical view; `drawn(floor, slot)` = does THIS client draw it.
 * Null when nothing can be walked to (a legacy room, an empty one).
 */
/**
 * WHAT IS NOT THE RESIDENTS' (slice E's rule, 2026-10-01): in a building that is a BOUND VENUE the staff side of the
 * counter belongs to the post's holder alone, and the customer's place and the queue are the venue's to fill — a
 * resident's itinerary never takes them, whether or not anyone holds the post.
 *   `staffFloors`  storeys whose employee-only anchors (`access: 'employee'`) are not dealt;
 *   `pieces`       `<floor>|<slot>` of pieces nobody of the household takes (the counter, the clinic's first cot);
 *   `points`       building-local points an anchor's spot and its approach point keep `r` metres clear of.
 */
export interface LifeReserve {
  staffFloors: ReadonlySet<number>;
  pieces: ReadonlySet<string>;
  points: ReadonlyArray<{ floor: number; x: number; z: number; r: number }>;
  /** THE LET ROOMS (slice H, 2026-10-02 — the tenant's door, interiors.md law 88): rectangles of the building's frame
   *  that are a TENANT's alone. An anchor whose spot OR approach point lies in one (padded by `rectPad` metres) is
   *  never dealt — no resident sleeps in a tenant's bed, and none is drawn walking through the shut leaf. Every client
   *  holds the same lease rows, so the itinerary stays the same on every client. */
  rects?: ReadonlyArray<{ floor: number; x0: number; z0: number; x1: number; z1: number }>;
  rectPad?: number;
}

export function lifeRoomOf(
  d: InteriorDescriptor, spec: Pick<InteriorSpec, 'floors' | 'stair' | 'lift'>,
  opts: { sockets: ReadonlyArray<LifeSocket>; shown: (floor: number, slot: string) => boolean; drawn?: (floor: number, slot: string) => boolean; service?: LiftService | null; reserve?: LifeReserve | null },
): LifeRoom | null {
  const floors = spec.floors;
  const st = spec.stair;
  const room: LifeRoom = {
    floors, anchors: [], toFoot: Array(floors).fill(null), toCall: Array(floors).fill(null), root: Array(floors).fill(null),
    stair: { x: st.x, z0: st.z - st.d / 2, z1: st.z + st.d / 2 }, lift: null,
  };
  const raw: LifeAnchor[] = [];
  for (const f of d.floors) {
    if (f.floor < 0 || f.floor >= floors) continue;
    const rootNode = f.routeNodes.find((n) => n.kind === (f.floor === 0 ? 'entry' : 'landing'));
    if (rootNode) room.root[f.floor] = { x: rootNode.x, z: rootNode.z };
    const routeTo = (id: string): Pt[] | null => { const r = f.routes.find((q) => q.to === id); return r && r.points.length > 0 ? r.points.map((p) => [p[0], p[1]] as Pt) : null; };
    room.toFoot[f.floor] = routeTo('stair-foot');
    room.toCall[f.floor] = routeTo('lift-call');
    if (f.source !== 'planned') continue; // a legacy storey's points are not proven free
    const res = opts.reserve ?? null;
    const kept = (x: number, z: number, ax: number, az: number): boolean => {
      if (!res) return true;
      for (const q of res.points) if (q.floor === f.floor && (Math.hypot(x - q.x, z - q.z) < q.r || Math.hypot(ax - q.x, az - q.z) < q.r)) return false;
      if (res.rects) { // a let room: its spot or the point a body stands at to use it
        const pad = res.rectPad ?? 0;
        const inside = (q: { x0: number; z0: number; x1: number; z1: number }, px: number, pz: number): boolean => px >= q.x0 - pad && px <= q.x1 + pad && pz >= q.z0 - pad && pz <= q.z1 + pad;
        for (const q of res.rects) if (q.floor === f.floor && (inside(q, x, z) || inside(q, ax, az))) return false;
      }
      return true;
    };
    for (const a of f.anchors) {
      const route = routeTo(a.id);
      if (!route) continue;
      if (res && ((a.piece !== undefined && res.pieces.has(`${f.floor}|${a.piece}`)) || (res.staffFloors.has(f.floor) && a.access === 'employee'))) continue;
      const pieceShown = !a.piece || opts.shown(f.floor, a.piece);
      const drawn = !a.piece || (opts.drawn ? opts.drawn(f.floor, a.piece) : pieceShown);
      const base = { key: `${f.floor}:${a.id}`, floor: f.floor, ax: a.ax, az: a.az, route, drawn };
      if (a.seat && (a.kind === 'bed' || a.kind === 'seat')) {
        if (!pieceShown) continue;
        const sock = opts.sockets.find((s) => s.floor === f.floor && s.slot === a.piece && Math.abs(s.x - a.seat!.x) < 0.02 && Math.abs(s.z - a.seat!.z) < 0.02);
        if (kept(a.seat.x, a.seat.z, a.ax, a.az)) raw.push({ ...base, use: a.seat.pose === 'lie' ? 'lie' : 'sit', x: a.seat.x, z: a.seat.z, yaw: a.seat.yaw, lift: sock ? sock.lift : a.seat.pose === 'lie' ? 0.5 : 0.45 });
      } else if (a.kind === 'work' && a.access !== 'guarded' && pieceShown) {
        if (!kept(a.seat ? a.seat.x : a.ax, a.seat ? a.seat.z : a.az, a.ax, a.az)) continue;
        if (a.seat && a.seat.pose === 'sit') {
          const sock = opts.sockets.find((s) => s.floor === f.floor && s.slot === a.piece && Math.abs(s.x - a.seat!.x) < 0.02 && Math.abs(s.z - a.seat!.z) < 0.02);
          raw.push({ ...base, use: 'work', seated: true, x: a.seat.x, z: a.seat.z, yaw: a.seat.yaw, lift: sock ? sock.lift : 0.45 });
        } else raw.push({ ...base, use: 'work', x: a.ax, z: a.az, yaw: a.facing, lift: 0 });
      }
      else if (a.kind === 'stand' && a.access === 'public' && pieceShown && kept(a.ax, a.az, a.ax, a.az)) raw.push({ ...base, use: 'stand', x: a.ax, z: a.az, yaw: a.facing, lift: 0 });
    }
  }
  const svc = opts.service;
  if (svc?.runs && spec.lift) {
    const served = svc.served.filter((f) => room.toCall[f]);
    if (served.length >= 2) {
      const c = cabRideRect(spec.lift);
      room.lift = { served, call: { x: spec.lift.call.x, z: spec.lift.call.z }, cab: { x: c.x, z: c.z }, faceDoor: spec.lift.call.facing + Math.PI };
    }
  }
  // Only storeys a resident can get to: the ground, then every storey joined to a reachable one by a stair run or the lift.
  const reach = new Set<number>([0]);
  for (let pass = 0; pass < floors; pass++) {
    for (let f = 0; f + 1 < floors; f++) {
      const run = !!room.toFoot[f] && !!room.root[f + 1];
      if (run && reach.has(f)) reach.add(f + 1);
      if (run && reach.has(f + 1)) reach.add(f);
    }
    if (room.lift && room.lift.served.some((f) => reach.has(f))) for (const f of room.lift.served) reach.add(f);
  }
  room.anchors = raw.filter((a) => reach.has(a.floor)).sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  return room.anchors.length > 0 ? room : null;
}

// ─── The residents and the deal ──────────────────────────────────────────────────────────────────────────────────────

export interface LifeResident {
  id: string;
  /** `versona_state.state` — only the home states are dealt, and only its CLASS decides where (`lifeClass`). */
  state: string;
  elder?: boolean; child?: boolean;
}
/** What a home state means to the itinerary. `resting` and `chatting` are ONE class: the server flips a resident
 *  between them every few seconds as company comes and goes, and a body must not change seats for it. */
export type LifeClass = 'asleep' | 'awake' | 'play';
export const lifeClass = (state: string): LifeClass => (state === 'sleeping' ? 'asleep' : state === 'playing' ? 'play' : 'awake');
/** The deal a resident is under in slot `k`: a sleeper's never changes; the others' every `MOVE_EVERY` slots, on a
 *  phase of their own. */
export function lifePeriod(id: string, state: string, k: number): number | 'keep' {
  if (lifeClass(state) === 'asleep') return 'keep';
  return Math.floor((k + (lifeHash(`${id}|phase`) % LIFE.MOVE_EVERY)) / LIFE.MOVE_EVERY);
}
/**
 * The kinds a state's CLASS reaches for, in order. `tod` = the sky's hour 0..1 (0 = midnight); `k` = the slot;
 * `counts` = how many anchors of each kind the room has. By day the awake pick their FIRST kind in proportion to what
 * the room offers (a room with two work points and seventy seats sends few to the work points): scarce kinds are what
 * make two residents want one anchor, and a contested anchor is what moves a third when somebody comes or goes. A bed
 * counts for a tenth of a seat by day (a lie-down), never as a fallback.
 */
export function wantedUses(state: string, tod: number, id: string, k: number, counts?: Partial<Record<AnchorUse, number>>): AnchorUse[] {
  const night = tod < 0.25 || tod >= 0.875;
  switch (lifeClass(state)) {
    case 'asleep': return ['lie', 'sit', 'stand'];
    case 'play': return ['stand', 'sit'];
    default: { // awake (resting / chatting)
      if (night) return ['sit', 'lie', 'stand'];
      // tenths: a seat, a work point and a standing point weigh 10 each; a bed weighs NAP_TENTHS — resting at home is
      // sometimes lying down on one's bed, and in a tower whose upper storeys are bedrooms that is what takes the
      // awake up the stairs and the lift by day
      const nSit = 10 * (counts ? counts.sit ?? 0 : 1), nWork = 10 * (counts ? counts.work ?? 0 : 1), nStand = 10 * (counts ? counts.stand ?? 0 : 1);
      const nLie = LIFE.NAP_TENTHS * (counts ? counts.lie ?? 0 : 0);
      const total = nSit + nWork + nStand + nLie;
      if (total <= 0) return ['sit', 'work', 'stand'];
      const h = lifeHash(`${id}|turn|${lifePeriod(id, state, k)}`) % total;
      return h < nSit ? ['sit', 'work', 'stand'] : h < nSit + nWork ? ['work', 'sit', 'stand'] : h < nSit + nWork + nStand ? ['stand', 'sit', 'work'] : ['lie', 'sit', 'stand', 'work'];
    }
  }
}
const byId = (a: LifeResident, b: LifeResident): number => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/** The deal for one slot: resident id → anchor (absent = nowhere to be). Injective: no anchor is dealt twice. */
export function dealSlot(room: LifeRoom, residents: ReadonlyArray<LifeResident>, k: number, tod: number): Map<string, LifeAnchor> {
  const out = new Map<string, LifeAnchor>(), taken = new Set<string>();
  const counts: Partial<Record<AnchorUse, number>> = {};
  for (const a of room.anchors) counts[a.use] = (counts[a.use] ?? 0) + 1;
  // THE SLEEPER KEEPS THE BED (slice H for slice U, 2026-10-02 — impl-upper.md): the ASLEEP are dealt first. Dealt in
  // id order, an awake resident with a lower id napping in a sleeper's lowest-hash bed took it, and the sleeper moved
  // when the nap ended (layout 2: 342 of 1,290 sleepers changed bed inside twelve slots). Then the id order, as before.
  const sleepFirst = (a: LifeResident, b: LifeResident): number => (lifeClass(a.state) === 'asleep' ? 0 : 1) - (lifeClass(b.state) === 'asleep' ? 0 : 1) || byId(a, b);
  for (const r of [...residents].sort(sleepFirst)) {
    const salt = `${r.id}|${lifePeriod(r.id, r.state, k)}`;
    let pick: LifeAnchor | null = null;
    for (const use of wantedUses(r.state, tod, r.id, k, counts)) {
      let best = Infinity;
      for (const a of room.anchors) {
        if (a.use !== use || taken.has(a.key)) continue;
        const h = lifeHash(`${salt}|${a.key}`);
        if (h < best) { best = h; pick = a; }
      }
      if (pick) break;
    }
    if (pick) { out.set(r.id, pick); taken.add(pick.key); }
  }
  return out;
}

// ─── Walks ───────────────────────────────────────────────────────────────────────────────────────────────────────────

export type LifeMode = 'walk' | 'stairs' | 'wait' | 'cab' | 'sit' | 'lie' | 'stand' | 'work';
interface Leg {
  kind: 'path' | 'hop' | 'stairs' | 'wait' | 'cab';
  dur: number;
  floor: number;
  pts?: Pt[]; len?: number;                                  // path (on `floor`)
  from?: { x: number; z: number; y: number }; to?: { x: number; z: number; y: number }; // hop / stairs / cab
  toFloor?: number;
}
export interface LifeTrip {
  id: string;
  from: LifeAnchor | null; to: LifeAnchor;
  depart: number;
  legs: Leg[];
  /** Total seconds (after the lift's waits are known). */
  dur: number;
  speed: number;
}
const polyLen = (p: Pt[]): number => { let s = 0; for (let i = 1; i < p.length; i++) s += Math.hypot(p[i][0] - p[i - 1][0], p[i][1] - p[i - 1][1]); return s; };
const same = (a: Pt, b: Pt): boolean => Math.abs(a[0] - b[0]) < 1e-6 && Math.abs(a[1] - b[1]) < 1e-6;
/** The walk between the ends of two polylines that start at one root: back along the first to their last common
 *  point, out along the second. Every segment is a segment of one of the two — the planner proved both. */
export function joinRoutes(a: Pt[], b: Pt[]): Pt[] {
  let i = 0;
  while (i + 1 < a.length && i + 1 < b.length && same(a[i + 1], b[i + 1])) i++;
  // a[i] === b[i] is the fork (i = 0: the root itself)
  const out: Pt[] = [];
  for (let j = a.length - 1; j >= i; j--) out.push(a[j]);
  for (let j = i + 1; j < b.length; j++) out.push(b[j]);
  return out;
}
function pathLeg(pts: Pt[], floor: number, speed: number): Leg | null {
  const len = polyLen(pts);
  return len < 0.02 ? null : { kind: 'path', pts, len, floor, dur: len / speed };
}

/** Does this anchor have a place of its own for the body (a seat, a bed, a desk's chair)? The body goes THERE on every
 *  client — the same spot, the same step to it — and is ON the piece only where the piece is drawn. */
const hasSpot = (a: LifeAnchor): boolean => a.use === 'sit' || a.use === 'lie' || !!a.seated;
const onPiece = (a: LifeAnchor): boolean => hasSpot(a) && a.drawn;
/** The step between a piece's approach point and the body's place on it: `SEAT_S` at least, and never faster than
 *  the body walks (a rooms-revision bed's approach point can stand two metres from its pillow). */
const hopS = (a: LifeAnchor, speed: number): number => Math.max(LIFE.SEAT_S, Math.hypot(a.x - a.ax, a.z - a.az) / Math.max(0.1, speed));
/** How high the body is at its spot: the seat's or the bed's height where it is drawn, the floor where it is not. */
const spotLift = (a: LifeAnchor): number => (a.drawn ? a.lift : 0);

/** The legs of a walk from one anchor (null = the door) to another. Null = no way (the caller keeps the body put). */
export function tripLegs(room: LifeRoom, from: LifeAnchor | null, to: LifeAnchor, speed: number): Leg[] | null {
  const legs: Leg[] = [];
  const push = (l: Leg | null): void => { if (l) legs.push(l); };
  const fa = from ? from.floor : 0;
  const startRoute: Pt[] = from ? from.route : room.root[0] ? [[room.root[0].x, room.root[0].z]] : [];
  if (startRoute.length === 0) return null;
  if (from && hasSpot(from)) {
    legs.push({ kind: 'hop', dur: hopS(from, speed), floor: fa, from: { x: from.x, z: from.z, y: floorY(fa) + spotLift(from) }, to: { x: from.ax, z: from.az, y: floorY(fa) } });
  }
  const fb = to.floor;
  if (fa === fb) push(pathLeg(joinRoutes(startRoute, to.route), fa, speed));
  else {
    const lift = room.lift;
    const byLift = !!lift && Math.abs(fb - fa) >= 2 && lift.served.includes(fa) && lift.served.includes(fb) && !!room.toCall[fa] && !!room.toCall[fb];
    if (byLift && lift) {
      push(pathLeg(joinRoutes(startRoute, room.toCall[fa]!), fa, speed));
      legs.push({ kind: 'wait', dur: 0, floor: fa, from: { x: lift.call.x, z: lift.call.z, y: floorY(fa) } });
      const ride = rideProfile(Math.abs(floorY(fb) - floorY(fa))).seconds;
      legs.push({ kind: 'cab', dur: 2 * LIFE.BOARD_S + (2 * LIFT.DOOR_TICKS) / LIFT_HZ + ride, floor: fa, toFloor: fb, from: { x: lift.call.x, z: lift.call.z, y: floorY(fa) }, to: { x: lift.cab.x, z: lift.cab.z, y: floorY(fb) } });
      push(pathLeg(joinRoutes(room.toCall[fb]!, to.route), fb, speed));
    } else {
      const up = fb > fa;
      let cur: Pt[] = startRoute;
      for (let f = fa; f !== fb; f += up ? 1 : -1) {
        const lower = up ? f : f - 1; // the run between `lower` and `lower + 1`
        const foot = room.toFoot[lower], head = room.root[lower + 1];
        if (!foot || !head) return null;
        const footPt = foot[foot.length - 1];
        const a = { x: footPt[0], z: footPt[1], y: floorY(lower) }, b = { x: head.x, z: head.z, y: floorY(lower + 1) };
        const flat = Math.abs(a.z - room.stair.z0) + Math.abs(b.z - room.stair.z1) + Math.abs(a.x - room.stair.x) + Math.abs(b.x - room.stair.x);
        const ramp = Math.hypot(room.stair.z1 - room.stair.z0, b.y - a.y);
        const dur = flat / speed + ramp / Math.min(speed, LIFE.RAMP_MPS);
        if (up) {
          push(pathLeg(joinRoutes(cur, foot), f, speed));
          legs.push({ kind: 'stairs', dur, floor: lower, toFloor: lower + 1, from: a, to: b });
          cur = [[head.x, head.z]];
        } else {
          // down: back to this storey's way in (the head of the run), down the run, onto the storey below at its foot
          push(pathLeg([...cur].reverse(), f, speed));
          legs.push({ kind: 'stairs', dur, floor: lower + 1, toFloor: lower, from: b, to: a });
          cur = foot;
        }
      }
      push(pathLeg(up ? joinRoutes(cur, to.route) : joinRoutes(cur, to.route), fb, speed));
    }
  }
  if (hasSpot(to)) {
    legs.push({ kind: 'hop', dur: hopS(to, speed), floor: fb, from: { x: to.ax, z: to.az, y: floorY(fb) }, to: { x: to.x, z: to.z, y: floorY(fb) + spotLift(to) } });
  }
  return legs;
}

// ─── The plan for one slot ───────────────────────────────────────────────────────────────────────────────────────────

export interface CabRide { id: string; from: number; to: number; start: number; end: number }
export interface LifePlan {
  k: number; slotStart: number;
  /** Where everyone ends up this slot, and the walk (if any) that takes them there. */
  at: Map<string, LifeAnchor>;
  trips: Map<string, LifeTrip>;
  /** The lift's rides this slot, in order. */
  rides: CabRide[];
  residents: Map<string, LifeResident>;
}
export const slotOf = (tS: number): number => Math.floor(tS / LIFE.SLOT_S);
const speedOf = (r: LifeResident): number => (r.child ? LIFE.CHILD_MPS : r.elder ? LIFE.ELDER_MPS : LIFE.WALK_MPS);

/**
 * Everything about slot `k`: who is where, who walks, when they set off, who rides the lift when. `todAt(tS)` = the
 * sky's hour (0..1) at an instant. Deterministic in (room, residents, k).
 */
export function planLife(room: LifeRoom, residentsIn: ReadonlyArray<LifeResident>, k: number, todAt: (tS: number) => number): LifePlan {
  const slotStart = k * LIFE.SLOT_S;
  const residents = residentsIn.filter((r) => LIFE_HOME_STATES.has(r.state)).sort(byId); // OUT is out
  const now = dealSlot(room, residents, k, todAt(slotStart));
  const before = dealSlot(room, residents, k - 1, todAt(slotStart - LIFE.SLOT_S));
  const plan: LifePlan = { k, slotStart, at: now, trips: new Map(), rides: [], residents: new Map(residents.map((r) => [r.id, r])) };
  for (const r of residents) {
    const to = now.get(r.id);
    if (!to) continue;
    // the only walks are the slot boundary's: from where the last slot's deal had them to where this one has them
    const from = before.get(r.id) ?? null;
    if (!from || from.key === to.key) continue; // stays where it is (or had nowhere to be: it is simply there)
    const depart = slotStart + (lifeHash(`${r.id}|go|${k}`) % (LIFE.STAGGER_S * 10)) / 10;
    const speed = speedOf(r);
    const legs = tripLegs(room, from, to, speed);
    if (!legs || legs.length === 0) continue; // no way between them: the body is simply there
    plan.trips.set(r.id, { id: r.id, from, to, depart, legs, dur: 0, speed });
  }
  // LEAVING FIRST: nobody arrives at an anchor before its last user has set off from it. A mover's departure is pushed
  // back until its walk (its legs without any lift wait — a wait only makes it later) ends `LEAVE_GAP_S` after that
  // user's begins. A chain settles in at most as many passes as it has links; two who swap leave together.
  const holder = new Map<string, string>();
  for (const [id, a] of before) holder.set(a.key, id);
  const walk0 = (t: LifeTrip): number => t.legs.reduce((sum, l) => sum + (l.kind === 'wait' ? 0 : l.dur), 0);
  for (let pass = 0; pass <= plan.trips.size; pass++) {
    let changed = false;
    for (const trip of plan.trips.values()) {
      const h = holder.get(trip.to.key);
      const ht = h !== undefined && h !== trip.id ? plan.trips.get(h) : undefined;
      if (!ht) continue; // nobody held it, or its holder is simply elsewhere from the slot's first instant
      const need = ht.depart + LIFE.LEAVE_GAP_S - walk0(trip);
      if (need > trip.depart + 1e-9) { trip.depart = need; changed = true; }
    }
    if (!changed) break;
  }
  // ONE CAR: rides in (ready, id) order; a later rider waits at the call post.
  const riders: Array<{ trip: LifeTrip; wait: Leg; cab: Leg; ready: number }> = [];
  for (const trip of plan.trips.values()) {
    let t = trip.depart;
    for (let i = 0; i < trip.legs.length; i++) {
      const l = trip.legs[i];
      if (l.kind === 'wait') riders.push({ trip, wait: l, cab: trip.legs[i + 1], ready: t });
      t += l.dur;
    }
  }
  riders.sort((a, b) => a.ready - b.ready || (a.trip.id < b.trip.id ? -1 : 1));
  let free = -Infinity, carAt = 0;
  for (const q of riders) {
    const from = q.cab.floor, to = q.cab.toFloor ?? from;
    // the car comes (empty, express) from where its last ride left it
    const come = rideProfile(Math.abs(floorY(from) - floorY(carAt)), LIFT.EXPRESS_MPS, LIFT.EXPRESS_ACCEL_MPS2).seconds;
    const start = Math.max(q.ready, free + come);
    q.wait.dur = start - q.ready;
    plan.rides.push({ id: q.trip.id, from, to, start, end: start + q.cab.dur });
    free = start + q.cab.dur; carAt = to;
  }
  for (const trip of plan.trips.values()) trip.dur = trip.legs.reduce((s, l) => s + l.dur, 0);
  return plan;
}

// ─── Where a resident is at time t ───────────────────────────────────────────────────────────────────────────────────

export interface LifeRig { state: string; seat: 'chair' | 'cot' | null; moving: boolean }
export interface LifePose {
  id: string;
  /** Building-local; `y` = metres above the building's ground. `yaw` in the plans' convention (rotation.y of a +Z model). */
  x: number; z: number; y: number; yaw: number;
  floor: number;
  mode: LifeMode;
  /** What the body's clip may be told (the never-mime law). */
  rig: LifeRig;
  /** The anchor it is at, or walking to. */
  anchor: string;
  /** Lying on a bed (the crowd twin reclines). */
  lying: boolean;
  /** In the lift car (the caller hides it while the player holds the car). */
  inCab: boolean;
}
const yawOf = (dx: number, dz: number, keep: number): number => (Math.abs(dx) + Math.abs(dz) > 1e-6 ? Math.atan2(dx, dz) : keep);
function settled(r: LifeResident, a: LifeAnchor): LifePose {
  const on = onPiece(a);
  const mode: LifeMode = on && a.use !== 'work' ? (a.use as 'sit' | 'lie') : a.use === 'work' ? 'work' : 'stand';
  const sleeping = r.state === 'sleeping';
  const rig: LifeRig = mode === 'lie' ? { state: 'sleeping', seat: 'cot', moving: false }
    : mode === 'sit' ? { state: sleeping ? 'dozing' : 'sitting', seat: 'chair', moving: false }
    : mode === 'work' ? (on ? { state: 'studying', seat: 'chair', moving: false } : { state: 'patrolling', seat: null, moving: false })
    : { state: sleeping ? 'dozing' : r.state, seat: null, moving: false }; // on the floor: a doze is cross-legged, never a mimed bed
  // a piece this client does not draw is not sat on: the body STANDS where the piece would be (its own spot — never
  // the approach point two seats of one sofa share)
  return {
    id: r.id, x: a.x, z: a.z, y: floorY(a.floor) + (on ? a.lift : 0), yaw: a.yaw, floor: a.floor,
    mode, rig, anchor: a.key, lying: mode === 'lie', inCab: false,
  };
}
function along(pts: Pt[], s: number): { x: number; z: number; yaw: number } {
  let left = s, yaw = 0;
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i][0] - pts[i - 1][0], dz = pts[i][1] - pts[i - 1][1], L = Math.hypot(dx, dz);
    if (L < 1e-9) continue;
    yaw = Math.atan2(dx, dz);
    if (left <= L) return { x: pts[i - 1][0] + (dx * left) / L, z: pts[i - 1][1] + (dz * left) / L, yaw };
    left -= L;
  }
  const e = pts[pts.length - 1];
  return { x: e[0], z: e[1], yaw };
}
const walkRig = (r: LifeResident): LifeRig => ({ state: r.state === 'sleeping' ? 'resting' : r.state, seat: null, moving: true });
const idleRig: LifeRig = { state: 'patrolling', seat: null, moving: false };

/** Where a resident is at `tS` under a plan (null = not placed by the itinerary: no anchor for them). */
export function poseAt(room: LifeRoom, plan: LifePlan, id: string, tS: number): LifePose | null {
  const r = plan.residents.get(id), to = plan.at.get(id);
  if (!r || !to) return null;
  const trip = plan.trips.get(id);
  if (!trip) return settled(r, to);
  let t = tS - trip.depart;
  if (t <= 0) return trip.from ? settled(r, trip.from) : settled(r, to); // not set off yet
  if (t >= trip.dur) return settled(r, to);
  for (const l of trip.legs) {
    if (t > l.dur) { t -= l.dur; continue; }
    const u = l.dur > 0 ? t / l.dur : 1;
    switch (l.kind) {
      case 'path': { const p = along(l.pts!, u * l.len!); return { id, x: p.x, z: p.z, y: floorY(l.floor), yaw: p.yaw, floor: l.floor, mode: 'walk', rig: walkRig(r), anchor: to.key, lying: false, inCab: false }; }
      case 'hop': {
        const a = l.from!, b = l.to!;
        return { id, x: a.x + (b.x - a.x) * u, z: a.z + (b.z - a.z) * u, y: a.y + (b.y - a.y) * u, yaw: yawOf(b.x - a.x, b.z - a.z, to.yaw), floor: l.floor, mode: 'walk', rig: walkRig(r), anchor: to.key, lying: false, inCab: false };
      }
      case 'stairs': {
        // foot → the ramp's start (flat), up the ramp, the ramp's end → the head (flat); time split by distance
        const a = l.from!, b = l.to!, st = room.stair;
        const lowIsA = a.y <= b.y;
        const lo = lowIsA ? a : b, hi = lowIsA ? b : a;
        const flatLo = Math.abs(lo.z - st.z0) + Math.abs(lo.x - st.x), flatHi = Math.abs(hi.z - st.z1) + Math.abs(hi.x - st.x);
        const ramp = Math.hypot(st.z1 - st.z0, hi.y - lo.y), slow = Math.min(trip.speed, LIFE.RAMP_MPS);
        const tLo = flatLo / trip.speed, tR = ramp / slow, tHi = flatHi / trip.speed, total = tLo + tR + tHi;
        let w = (lowIsA ? u : 1 - u) * total; // time measured from the LOW end
        let x: number, z: number, y: number;
        if (w <= tLo) { const q = tLo > 0 ? w / tLo : 1; x = lo.x + (st.x - lo.x) * q; z = lo.z + (st.z0 - lo.z) * q; y = lo.y; }
        else if (w <= tLo + tR) { const q = (w - tLo) / tR; x = st.x; z = st.z0 + (st.z1 - st.z0) * q; y = lo.y + (hi.y - lo.y) * q; }
        else { w -= tLo + tR; const q = tHi > 0 ? w / tHi : 1; x = st.x + (hi.x - st.x) * q; z = st.z1 + (hi.z - st.z1) * q; y = hi.y; }
        return { id, x, z, y, yaw: lowIsA ? 0 : Math.PI, floor: y >= hi.y - 1e-6 ? Math.max(l.floor, l.toFloor ?? l.floor) : Math.min(l.floor, l.toFloor ?? l.floor), mode: 'stairs', rig: walkRig(r), anchor: to.key, lying: false, inCab: false };
      }
      case 'wait': {
        const a = l.from!;
        return { id, x: a.x, z: a.z, y: a.y, yaw: (room.lift?.faceDoor ?? 0) + Math.PI, floor: l.floor, mode: 'wait', rig: idleRig, anchor: to.key, lying: false, inCab: false };
      }
      case 'cab': {
        const call = l.from!, cab = l.to!, doorS = LIFT.DOOR_TICKS / LIFT_HZ;
        const y0 = floorY(l.floor), y1 = floorY(l.toFloor ?? l.floor);
        const rideS = l.dur - 2 * LIFE.BOARD_S - 2 * doorS;
        const face = room.lift?.faceDoor ?? 0;
        if (t < LIFE.BOARD_S) { const q = t / LIFE.BOARD_S; return { id, x: call.x + (cab.x - call.x) * q, z: call.z + (cab.z - call.z) * q, y: y0, yaw: yawOf(cab.x - call.x, cab.z - call.z, face), floor: l.floor, mode: 'walk', rig: walkRig(r), anchor: to.key, lying: false, inCab: q > 0.5 }; }
        let w = t - LIFE.BOARD_S;
        if (w < doorS) return { id, x: cab.x, z: cab.z, y: y0, yaw: face, floor: l.floor, mode: 'cab', rig: idleRig, anchor: to.key, lying: false, inCab: true };
        w -= doorS;
        if (w < rideS) { const p = rideProfile(Math.abs(y1 - y0)); return { id, x: cab.x, z: cab.z, y: y0 + Math.sign(y1 - y0) * p.at(Math.round(w * LIFT_HZ)), yaw: face, floor: l.floor, mode: 'cab', rig: idleRig, anchor: to.key, lying: false, inCab: true }; }
        w -= rideS;
        if (w < doorS) return { id, x: cab.x, z: cab.z, y: y1, yaw: face, floor: l.toFloor ?? l.floor, mode: 'cab', rig: idleRig, anchor: to.key, lying: false, inCab: true };
        w -= doorS;
        const q = Math.min(1, w / LIFE.BOARD_S);
        return { id, x: cab.x + (call.x - cab.x) * q, z: cab.z + (call.z - cab.z) * q, y: y1, yaw: yawOf(call.x - cab.x, call.z - cab.z, face), floor: l.toFloor ?? l.floor, mode: 'walk', rig: walkRig(r), anchor: to.key, lying: false, inCab: q < 0.5 };
      }
    }
  }
  return settled(r, to);
}

/** The lift car the RESIDENTS' rides imply at `tS`: its floor height, its doors (0 shut … 1 open) and the storey it is
 *  docked at — null when no resident ride is in progress (the car is wherever the player's machine has it). */
export function residentCabAt(plan: LifePlan, tS: number): { y: number; door: number; floor: number } | null {
  const doorS = LIFT.DOOR_TICKS / LIFT_HZ;
  for (const ride of plan.rides) {
    if (tS < ride.start || tS >= ride.end) continue;
    const y0 = floorY(ride.from), y1 = floorY(ride.to);
    let w = tS - ride.start;
    if (w < LIFE.BOARD_S) return { y: y0, door: 1, floor: ride.from };
    w -= LIFE.BOARD_S;
    if (w < doorS) return { y: y0, door: 1 - w / doorS, floor: ride.from };
    w -= doorS;
    const rideS = ride.end - ride.start - 2 * LIFE.BOARD_S - 2 * doorS;
    if (w < rideS) return { y: y0 + Math.sign(y1 - y0) * rideProfile(Math.abs(y1 - y0)).at(Math.round(w * LIFT_HZ)), door: 0, floor: ride.from };
    w -= rideS;
    if (w < doorS) return { y: y1, door: w / doorS, floor: ride.to };
    return { y: y1, door: 1, floor: ride.to };
  }
  return null;
}

/** Everyone at once (the gate, the bed): poses by id for the plan of the slot `tS` falls in. */
export function itineraryAt(room: LifeRoom, residents: ReadonlyArray<LifeResident>, tS: number, todAt: (tS: number) => number): Map<string, LifePose> {
  const plan = planLife(room, residents, slotOf(tS), todAt);
  const out = new Map<string, LifePose>();
  for (const r of residents) { const p = poseAt(room, plan, r.id, tS); if (p) out.set(r.id, p); }
  return out;
}

// ─── What the lift module and the residents' drawing share (globalThis-backed: the instance-split law) ───────────────

export interface LiftShare {
  /** The interior both are about. */
  id: string | null;
  /** The car the residents' rides imply right now (written by the residents' drawing). */
  residentCab: { y: number; door: number; floor: number } | null;
  /** The player is using the car (written by the lift module): residents inside it are not drawn. */
  playerBusy: boolean;
  /** What the lift really serves in this visit (written by the lift module; null = no car in this shell). */
  service: LiftService | null;
}
export function liftShare(): LiftShare {
  const g = globalThis as unknown as { __atlasLiftShare?: LiftShare };
  return (g.__atlasLiftShare ??= { id: null, residentCab: null, playerBusy: false, service: null });
}
